import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import CloudDownloadIcon from '@heroicons/react/outline/esm/CloudDownloadIcon.js'
import ArrowLeftLineIcon from '@heroicons/react/solid/esm/ArrowLeftIcon.js'
import ArrowRightLineIcon from '@heroicons/react/solid/esm/ArrowRightIcon.js'

import { CloseButton, IconButton } from '../Button/index.js'
import { usePreloadImage } from '../hooks/usePreloadImage.js'
import { Modal } from '../Modal/index.js'
import { useKeypressListener } from '../utils/useKeypressListener.js'
import { MediaZoomContext } from './MediaZoomContext.js'
import { MediaZoomItem } from './MediaZoomItem.js'
import { ZoomableItem } from './types.js'
import { downloadFile } from './utils.js'

export interface MediaZoomProps {
  children?: ReactNode

  /**
   * Allow to open full-sized image
   */
  zoom?: boolean
  /**
   * Adds UI control for downloading the original image of the current slide
   */
  download?: boolean
}

export const MediaZoom = ({ children, zoom, download }: MediaZoomProps) => {
  const [zoomed, setZoomed] = useState(false)

  const items = useRef<ZoomableItem[]>([])
  const [activeIndex, setActiveIndex] = useState(0)

  const toggle = useCallback(() => {
    setZoomed(!zoomed)
  }, [zoomed])

  const register = useCallback((item: ZoomableItem) => {
    if (!items.current.map(it => it.id).includes(item.id)) {
      items.current.push(item)
    }
  }, [])

  const unregister = useCallback((id: string) => {
    items.current = items.current.filter(it => it.id !== id)
  }, [])

  const openItem = useCallback((id: string) => {
    setActiveIndex(items.current.findIndex(it => it.id === id))
    setZoomed(true)
  }, [])

  const contextValue = useMemo(
    () => ({
      zoomEnabled: zoom,
      zoomed,
      activeIndex,
      register,
      unregister,
      toggle,
      openItem,
    }),
    [zoom, zoomed, activeIndex, register, unregister, toggle, openItem],
  )

  const activeItem = items.current[activeIndex]
  const { length } = items.current
  const isPreviousButtonEnabled = activeIndex > 0
  const isNextButtonEnabled = activeIndex < length - 1

  const goToPrevious = () => {
    if (isPreviousButtonEnabled) {
      setActiveIndex(activeIndex - 1)
    }
  }

  const goToNext = () => {
    if (isNextButtonEnabled) {
      setActiveIndex(activeIndex + 1)
    }
  }

  useKeypressListener('ArrowLeft', goToPrevious)
  useKeypressListener('ArrowRight', goToNext)

  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    const slide = activeItem
    if (!slide?.original && !slide?.downloadUrl) {
      return
    }
    setDownloading(true)

    if (slide.downloadUrl) {
      await downloadFile(slide.downloadUrl, slide.caption)
    } else {
      await downloadFile(slide.original)
    }

    setDownloading(false)
  }

  usePreloadImage({ src: activeItem?.original })

  return (
    <MediaZoomContext.Provider value={contextValue}>
      {children}
      {zoom && activeItem && (
        <Modal open={zoomed} onClose={toggle} showCloseBtn={false} size="full">
          <Modal.Header className="space-s-3">
            <div className="overflow-hidden grow">
              {activeItem.caption && (
                <p className="truncate">{activeItem.caption}</p>
              )}
            </div>

            {download && (
              <IconButton
                size="lg"
                variant="tertiaryNeutral"
                disabled={downloading}
                loading={downloading}
                icon={<CloudDownloadIcon />}
                onClick={handleDownload}
                tabIndex={-1}
              />
            )}
            <CloseButton size="lg" onClick={toggle} />
          </Modal.Header>
          <Modal.Content className="flex flex-col justify-center">
            <div className="grow min-h-0 flex items-center justify-center space-s-3 py-3">
              {length > 1 && (
                <IconButton
                  variant="secondaryNeutral"
                  rounded
                  onClick={goToPrevious}
                  disabled={!isPreviousButtonEnabled}
                  icon={<ArrowLeftLineIcon className="rtl:-scale-x-100" />}
                  className="shrink-0"
                />
              )}

              <div className="grow min-w-0 h-full">
                <img
                  className="max-h-[calc(100vh-10rem)] mx-auto object-scale-down"
                  width={activeItem.originalWidth}
                  height={activeItem.originalHeight}
                  src={activeItem.original}
                  alt={activeItem.alt}
                />
              </div>

              {length > 1 && (
                <IconButton
                  variant="secondaryNeutral"
                  rounded
                  onClick={goToNext}
                  disabled={!isNextButtonEnabled}
                  icon={<ArrowRightLineIcon className="rtl:-scale-x-100" />}
                  className="shrink-0"
                />
              )}
            </div>
          </Modal.Content>
        </Modal>
      )}
    </MediaZoomContext.Provider>
  )
}

MediaZoom.Item = MediaZoomItem
